import { useEffect, useRef, useState } from "react";

/**
 * Slot-machine counter.
 * - Animates 0 → value over `duration` (default 1500ms)
 * - Easing matches cubic-bezier(0.25, 1, 0.5, 1) (ease-out quart)
 * - Triggered by a native IntersectionObserver — replays every time the
 *   element re-enters the viewport (reset to 0 on exit, then animate on
 *   re-entry).
 * - Works reliably on mobile, tablet, and desktop.
 */
const RollingCounter = ({
  value = 0,
  duration = 1500,
  suffix = "",
  prefix = "",
  format = true,
  testId,
  className = "",
}) => {
  const ref = useRef(null);
  const rafRef = useRef(null);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // cubic-bezier(0.25, 1, 0.5, 1) ≈ ease-out quartic
    const easeOut = (t) => 1 - Math.pow(1 - t, 4);

    const cancel = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const runAnimation = () => {
      cancel();
      setDisplay(0);
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = easeOut(p);
        setDisplay(Math.floor(eased * value));
        if (p < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          setDisplay(value);
          rafRef.current = null;
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            runAnimation();
          } else {
            // Reset so the animation can replay on next entry.
            cancel();
            setDisplay(0);
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      cancel();
    };
  }, [value, duration]);

  const formatted = format ? display.toLocaleString("en-IN") : String(display);

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

export default RollingCounter;
